/**
 * HeroSection Component
 * Hero section with value proposition and CTA
 */

import { TIER_PICKS_QUOTAS } from '@parlay-picker/shared/subscriptionTier';
import { Activity, Target, TrendingUp } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

export function HeroSection(): ReactElement {
  return (
    <section className="relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-theme-primary/10 via-theme-bg to-theme-bg" />

      <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-32">
        <div className="text-center max-w-3xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-theme-surface border border-theme-border mb-8">
            <span className="w-2 h-2 bg-theme-success rounded-full animate-pulse" />
            <span className="text-sm text-theme-text-secondary">
              AI-Powered Basketball Betting Analysis
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl md:text-6xl font-bold text-theme-text mb-6 leading-tight">
            Generate Winning Basketball Parlays with{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-theme-primary to-theme-primary-hover">
              AI-Powered Analysis
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-theme-text-secondary mb-10 leading-relaxed">
            Smart prop picks, real-time tracking, and detailed analytics. Find edges the sportsbooks
            miss with data-driven basketball betting analysis.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link
              to="/register"
              className="px-8 py-4 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill text-lg font-semibold rounded-lg shadow-lg shadow-theme-primary/25 transition transform hover:scale-105"
            >
              Start Free Today
            </Link>
            <a
              href="#features"
              className="px-8 py-4 bg-theme-surface hover:bg-theme-surface-hover text-theme-text text-lg font-semibold rounded-lg border border-theme-border transition"
            >
              See How It Works
            </a>
          </div>

          {/* Social proof */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-theme-text-tertiary">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-theme-success" />
              <span>{TIER_PICKS_QUOTAS.free.standard} Free Picks Daily</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-theme-primary" />
              <span>Real-Time Settlement</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-theme-warning" />
              <span>Best Line Finder</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
